import { useState, useRef, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from 'primereact/fileupload';
import { Image } from 'primereact/image';
import { insertarBanner, editarBanner } from '../../../../services/backend/bannerService';
import { BannerFamiliaTable } from './BannerFamiliaTable';
import { BannerProductoTable } from './BannerProductoTable';
import { getFamilias } from '../../../../services/backend/catalogService';
import { flattenFamilias } from '../shared/flattenFamilias';
import { getProductosByCriteria } from '../../../../services/backend/productoService';
import { ensureCatalogAuth } from '../../../../services/backend/authService';
import AppToast from '../../../../shared/components/AppToast';
import useFormState from '../../../../shared/hooks/useFormState';

export const BannerDialog = ({ visible, onCancel, onSave, banner, defaultTipo = '' }) => {
    const isEdit = !!banner;
    const fileUploadRef = useRef(null);
    const toastRef = useRef(null);

    const { values, setField, reset } = useFormState({
        id: null,
        llave: '',
        tipo: '',
        clase: 0,
        titulo: '',
        subtitulo: '',
        texto: '',
        boton: '',
    });

    const [foto, setFoto] = useState(null);
    const [fotoPreview, setFotoPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [familias, setFamilias] = useState([]);
    const [productos, setProductos] = useState([]);
    const [loadingFamilias, setLoadingFamilias] = useState(false);
    const [loadingProductos, setLoadingProductos] = useState(false);
    const bannerRef = useRef(null);
    const altaRef = useRef(false);

    useEffect(() => {
        if (visible && banner && banner !== bannerRef.current) {
            bannerRef.current = banner;
            altaRef.current = false;
            reset({
                id: banner.id || banner.codigo || null,
                llave: banner.llave || '',
                tipo: banner.tipo || '',
                clase: banner.clase ?? 0,
                titulo: banner.titulo || '',
                subtitulo: banner.subtitulo || '',
                texto: banner.texto || '',
                boton: banner.boton || '',
            });
            setFoto(null);
            if (banner.foto && banner.mime) {
                setFotoPreview(`data:${banner.mime};base64,${banner.foto}`);
            } else {
                setFotoPreview(null);
            }
            if (fileUploadRef.current) fileUploadRef.current.clear();
        }
        if (visible && !banner && !altaRef.current) {
            altaRef.current = true;
            bannerRef.current = null;
            reset({
                id: null,
                llave: '',
                tipo: defaultTipo || '',
                clase: 0,
                titulo: '',
                subtitulo: '',
                texto: '',
                boton: '',
            });
            setFoto(null);
            setFotoPreview(null);
            if (fileUploadRef.current) fileUploadRef.current.clear();
        }
        if (!visible) {
            bannerRef.current = null;
            altaRef.current = false;
            setFotoPreview(null);
        }
    }, [visible, banner, defaultTipo]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!visible || !values.tipo) {
            setFamilias([]);
            setProductos([]);
            return;
        }

        let mounted = true;

        const loadData = async () => {
            setLoadingFamilias(true);
            setLoadingProductos(true);
            try {
                await ensureCatalogAuth();
                const [familiasRes, productosRes] = await Promise.all([
                    getFamilias({ tipo: values.tipo }),
                    getProductosByCriteria({ tipo: values.tipo, familia: 0, start: 0, length: 9999 })
                ]);
                if (!mounted) return;
                const rawFamilias = Array.isArray(familiasRes?.familias) ? familiasRes.familias : Array.isArray(familiasRes) ? familiasRes : [];
                const familiasData = flattenFamilias(rawFamilias)
                    .filter(f => f.activa)
                    .map(f => ({ id: f.value, nombre: f.label }));
                const productosData = Array.isArray(productosRes?.productos) ? productosRes.productos : Array.isArray(productosRes) ? productosRes : [];
                setFamilias(familiasData);
                setProductos(productosData);
            } catch (err) {
                if (!mounted) return;
                setFamilias([]);
                setProductos([]);
            } finally {
                if (mounted) {
                    setLoadingFamilias(false);
                    setLoadingProductos(false);
                }
            }
        };

        loadData();
        return () => { mounted = false; };
    }, [visible, values.tipo]);

    const handleSelectFamilia = (familiaId) => {
        if (!familiaId) {
            setField('llave', '');
            setField('clase', 0);
        } else {
            setField('llave', String(familiaId));
            setField('clase', 1);
        }
        };

        const handleSelectProducto = (productoCodigo) => {
            if (!productoCodigo) {
                setField('llave', '');
                setField('clase', 0);
            } else {
                setField('llave', String(productoCodigo));
                setField('clase', 2);
        }
    };

    const handleSelectFoto = ({ files }) => {
        const file = files?.[0] || null;
        if (!file) return;
        if (file.type !== 'image/jpeg' && !/\.(jpg|jpeg)$/i.test(file.name)) {
            toastRef.current?.show({ severity: 'error', summary: 'Formato inválido', detail: 'Solo se permiten imágenes .jpg o .jpeg.', life: 4000 });
            fileUploadRef.current?.clear();
            return;
        }
        setFoto(file);
        setFotoPreview(URL.createObjectURL(file));
    };

    const handleCancel = () => {
        reset({ id: null, llave: '', tipo: '', clase: 0, titulo: '', subtitulo: '', texto: '', boton: '' });
        setFoto(null);
        setFotoPreview(null);
        if (fileUploadRef.current) fileUploadRef.current.clear();
        onCancel();
    };

    const handleSave = async () => {
        if (!values.tipo || (!isEdit && !foto)) {
            toastRef.current?.show({ severity: 'warn', summary: 'Campos obligatorios', detail: 'Complete tipo y foto', life: 3000 });
            return;
        }

        const llaveFinal = values.llave || '0';
        const claseFinal = values.clase || 0;

        const formData = new FormData();
        if (isEdit) formData.append('id', values.id);
        formData.append('llave', llaveFinal);
        formData.append('tipo', values.tipo);
        formData.append('clase', String(claseFinal));
        formData.append('titulo', values.titulo);
        formData.append('subtitulo', values.subtitulo);
        formData.append('texto', values.texto);
        formData.append('boton', values.boton);
        if (foto) {
            formData.append('foto', foto);
        } else if (isEdit && banner?.foto && banner?.mime) {
            const byteString = atob(banner.foto);
            const ab = new ArrayBuffer(byteString.length);
            const ia = new Uint8Array(ab);
            for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
            const blob = new Blob([ab], { type: banner.mime });
            formData.append('foto', blob, `banner.${banner.mime.split('/')[1]}`);
        }

        setSaving(true);
        try {
            if (isEdit) {
                await editarBanner(formData);
            } else {
                await insertarBanner(formData);
            }
            reset({ id: null, llave: '', tipo: '', clase: 0, titulo: '', subtitulo: '', texto: '', boton: '' });
            onSave?.();
        } catch (err) {
            toastRef.current?.show({ severity: 'error', summary: 'Error', detail: err?.body?.mensaje || err?.mensaje || 'Error al guardar banner', life: 4000 });
        } finally {
            setSaving(false);
        }
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button className="p-button-text" label="Cancelar" onClick={handleCancel} disabled={saving} />
            <Button className="p-button" label={isEdit ? "Guardar" : "Agregar"} onClick={handleSave} loading={saving} />
        </div>
    );

    return (
        <Dialog
            header={isEdit ? "Editar Banner" : "Agregar Banner"}
            visible={visible}
            style={{ width: '52rem', maxWidth: '95vw' }}
            onHide={handleCancel}
            footer={footer}
        >
            <AppToast ref={toastRef} />
            <div className="pt-3">
                <div className="formgrid grid align-items-center mb-4">
                    <label htmlFor="dlg-tipo" className="col-12 md:col-2 text-lg">Tipo <span className="text-red-500">*</span></label>
                    <div className="col-12 md:col-4">
                        <Dropdown id="dlg-tipo" value={values.tipo} onChange={(e) => setField('tipo', e.value)} options={['B2B','B2C']} placeholder="Seleccionar" className="w-full" />
                    </div>
                </div>
              
                    
                <div className="formgrid grid align-items-center mb-4">
                    <label htmlFor="dlg-titulo" className="col-12 md:col-2 text-lg">Destino del botón:</label>
                    {values.clase === 1 && (
                        <span className="text-sm font-medium px-2 py-1 border-round bg-green-100 text-green-700 flex align-items-center gap-3">
                            Familia
                            <i className="pi pi-times-circle cursor-pointer text-green-600 hover:text-green-900" onClick={() => handleSelectFamilia(null)} />
                        </span>
                    )}
                    {values.clase === 2 && (
                        <span className="text-sm font-medium px-2 py-1 border-round bg-green-100 text-green-700 flex align-items-center gap-3">
                            Producto
                            <i className="pi pi-times-circle cursor-pointer text-green-600 hover:text-green-900" onClick={() => handleSelectProducto(null)} />
                        </span>
                    )}
                    {values.clase === 0 && <span className="text-sm font-medium px-2 py-1 border-round bg-gray-100 text-gray-500">Sin selección</span>}
                </div>
        
              

                <div className="mb-4">
                    <BannerFamiliaTable
                        familias={familias}
                        loading={loadingFamilias}
                        selectedId={values.clase === 1 ? values.llave : null}
                        onSelect={handleSelectFamilia}
                        disabled={values.clase === 2}
                    />

                    <BannerProductoTable
                        productos={productos}
                        loading={loadingProductos}
                        selectedCodigo={values.clase === 2 ? values.llave : null}
                        onSelect={handleSelectProducto}
                        disabled={values.clase === 1}
                    />
                </div>
                
                <div className="formgrid grid align-items-center mb-4">
                    <label htmlFor="dlg-titulo" className="col-12 md:col-2 text-lg">Título</label>
                    <div className="col-12 md:col-10">
                        <InputText id="dlg-titulo" value={values.titulo} onChange={(e) => setField('titulo', e.target.value)} className="w-full" maxLength={100} />
                    </div>
                </div>
                <div className="formgrid grid align-items-center mb-4">
                    <label htmlFor="dlg-subtitulo" className="col-12 md:col-2 text-lg">Subtítulo</label>
                    <div className="col-12 md:col-10">
                        <InputText id="dlg-subtitulo" value={values.subtitulo} onChange={(e) => setField('subtitulo', e.target.value)} className="w-full" maxLength={100} />
                    </div>
                </div>
                <div className="formgrid grid align-items-center mb-4">
                    <label htmlFor="dlg-texto" className="col-12 md:col-2 text-lg">Texto</label>
                    <div className="col-12 md:col-10">
                        <InputTextarea id="dlg-texto" value={values.texto} onChange={(e) => setField('texto', e.target.value)} className="w-full" maxLength={250} />
                    </div>
                </div>
                <div className="formgrid grid align-items-center mb-4">
                    <label htmlFor="dlg-boton" className="col-12 md:col-2 text-lg">Botón</label>
                    <div className="col-12 md:col-4">
                        <InputText id="dlg-boton" value={values.boton} onChange={(e) => setField('boton', e.target.value)} className="w-full" maxLength={45} />
                    </div>
                </div>
                <div className="formgrid grid align-items-center mb-4">
                    <label htmlFor="dlg-foto" className="col-12 md:col-2 text-lg">Foto {!isEdit && <span className="text-red-500">*</span>}</label>
                    <div className="col-12 md:col-10 mt-3">         
                        
                        {fotoPreview && (
                            <Image src={fotoPreview} alt="Preview" preview className="mt-2 border-round" imageClassName="w-full" imageStyle={{ maxHeight: '120px', objectFit: 'contain' }} />
                        )}

                        <FileUpload
                            ref={fileUploadRef}
                            customUpload
                            auto
                            uploadHandler={handleSelectFoto}
                            accept=".jpg,.jpeg"
                            chooseLabel="Seleccionar"
                            uploadOptions={{ style: { display: 'none' } }}
                            cancelOptions={{ style: { display: 'none' } }}
                            emptyTemplate={<p className="m-0">Arrastra o selecciona una foto. Sugerencia: imagen de 1200px de ancho. (JPG/JPEG)</p>}
                            disabled={saving}
                        />
               
                    </div>
                </div>
            </div>
        </Dialog>
    );
};
